import { X, CheckCircle, Clock, ChevronRight, RotateCcw } from 'lucide-react'
import type { TourDefinition, TourProgress } from '../types'

const ROLE_DEFAULTS: Record<string, string> = {
  tenant_owner: 'clusters',
  tenant_admin: 'clusters',
  tenant_operator: 'workflows',
  tenant_viewer: 'dashboard',
  platform_admin: 'dashboard',
}

const TOUR_CATEGORIES: Record<string, string[]> = {
  'Getting Started': ['onboarding', 'dashboard'],
  'Infrastructure': ['clusters', 'dagster'],
  'AI & Tools': ['mcp-tools', 'ai-governance'],
  'Operations': ['workflows', 'cost', 'notifications', 'audit'],
}

interface Props {
  tours: TourDefinition[]
  progress: TourProgress
  userRole: string | null
  onStartTour: (tourId: string) => void
  onReset: () => void
  onClose: () => void
}

export function TourMenu({ tours, progress, userRole, onStartTour, onReset, onClose }: Props) {
  const tourMap = new Map(tours.map((t) => [t.id, t]))
  const defaultTourId = userRole ? ROLE_DEFAULTS[userRole] : 'onboarding'
  const suggested = tourMap.get(defaultTourId) ?? tours.find((t) => !progress[t.id]?.completed)

  const completedCount = Object.values(progress).filter((p) => p.completed).length

  return (
    <div className="fixed right-0 top-0 z-[9000] flex h-full w-80 flex-col border-l border-gray-700 bg-gray-900 shadow-2xl">
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-4">
        <div>
          <h2 className="text-base font-semibold text-gray-100">Product Tours</h2>
          <p className="mt-0.5 text-xs text-gray-400">
            {completedCount}/{tours.length} completed
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-800 hover:text-gray-100"
          aria-label="Close tour menu"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {suggested && !progress[suggested.id]?.completed && (
          <section className="mb-6">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Suggested for you
            </h3>
            <TourItem
              tour={suggested}
              completed={false}
              onStart={() => onStartTour(suggested.id)}
            />
          </section>
        )}

        <section>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
            All tours
          </h3>
          <div className="space-y-6">
            {Object.entries(TOUR_CATEGORIES).map(([category, tourIds]) => {
              const categoryTours = tourIds
                .map((id) => tourMap.get(id))
                .filter((t): t is TourDefinition => !!t)
              if (!categoryTours.length) return null
              return (
                <div key={category}>
                  <p className="mb-2 text-xs font-medium text-gray-600">{category}</p>
                  <div className="space-y-2">
                    {categoryTours.map((tour) => (
                      <TourItem
                        key={tour.id}
                        tour={tour}
                        completed={progress[tour.id]?.completed ?? false}
                        onStart={() => onStartTour(tour.id)}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      </div>

      <div className="border-t border-gray-700 px-4 py-3">
        <button
          onClick={onReset}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300"
        >
          <RotateCcw className="h-3 w-3" />
          Reset all progress
        </button>
      </div>
    </div>
  )
}

interface TourItemProps {
  tour: TourDefinition
  completed: boolean
  onStart: () => void
}

function TourItem({ tour, completed, onStart }: TourItemProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-700 bg-gray-800 p-3">
      <div className="flex-shrink-0">
        {completed ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <div className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-600 text-xs text-gray-500">
            <ChevronRight className="h-3 w-3" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-200 truncate">{tour.title}</p>
        <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
          <Clock className="h-3 w-3" />
          <span>{tour.estimatedMinutes} min</span>
          {completed && <span className="ml-1 text-green-600 dark:text-green-400">Completed</span>}
        </div>
      </div>
      <button
        onClick={onStart}
        className="flex-shrink-0 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700"
      >
        {completed ? 'Replay' : 'Start'}
      </button>
    </div>
  )
}
